import {
  signalStore,
  withState,
  patchState,
  withMethods,
  withComputed,
  withProps,
} from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { withLogger } from '@ngrx-traits/signals';
import { isOAuthEnabled, OAuthProvider } from '@core/auth/oauth/oauth.config';
import { OAuthStore } from '@core/auth/oauth/oauth.store';
import { AuthStore } from '@core/user';

interface LoginFormState {
  email: string;
  password: string;
}

const initialState: LoginFormState = {
  email: '',
  password: '',
};

type LoginStatus = 'oauth-error' | 'login-error' | 'loading' | 'idle';

export const LoginFormStore = signalStore(
  withState(initialState),
  withLogger({ name: 'LoginFormStore' }),
  withProps(() => ({
    _authStore: inject(AuthStore),
    _oauthStore: isOAuthEnabled() ? inject(OAuthStore) : null,
  })),
  withComputed(({ email, password, _oauthStore, _authStore }) => ({
    formData: computed(() => ({ email: email(), password: password() })),
    oauthError: computed(() => _oauthStore?.error() ?? null),
    isLoading: computed(() => _authStore.isLoginLoading()),
    loginError: computed(() => _authStore.loginError()),
    loginStatus: computed<LoginStatus>(() => {
      if (_oauthStore?.hasError()) return 'oauth-error';
      if (_authStore.loginError()) return 'login-error';
      if (_authStore.isLoginLoading()) return 'loading';
      return 'idle';
    }),
  })),
  withMethods((store) => ({
    updateFormData(data: Partial<LoginFormState>) {
      patchState(store, data);
      if (store._oauthStore?.hasError()) {
        store._oauthStore.clearError();
      }
    },
    setEmail(email: string) {
      patchState(store, { email });
      if (store._oauthStore?.hasError()) {
        store._oauthStore.clearError();
      }
    },
    setPassword(password: string) {
      patchState(store, { password });
      if (store._oauthStore?.hasError()) {
        store._oauthStore.clearError();
      }
    },
    reset() {
      patchState(store, initialState);
    },
    submitLogin() {
      store._authStore.login({
        Email: store.email(),
        Password: store.password(),
      });
    },
    initiateOAuthLogin(provider: OAuthProvider = 'primary') {
      store._authStore.initiateOAuthLogin(provider);
    },
  }))
);
